import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { getPersonnelRoles, getRoomTimingName } from 'shared/helpers';
import { AlertNotice, SearchForm, ModalInfo, EventDetails, SimpleModal } from 'shared/components';
import PlusIcon from 'assets/svgs/plusIcon.svg';
import { isEqual, isEmpty } from 'lodash';
import './AssignStaff.scss';
import { TooltipItem } from 'shared/components';

const searchStaff = (staffList, value) =>
  staffList.filter((staff) => {
    const fullName = staff.lastName.trim().concat(', ', staff.firstName.trim());
    const emailAddress = staff.emailAddress.trim();
    const newValue = value.toLowerCase();

    return (
      fullName.toLowerCase().indexOf(newValue) !== -1 ||
      emailAddress.toLowerCase().indexOf(newValue) !== -1
    );
  });

const isCurrentUser = (currentAssignedStaff, staff) =>
  currentAssignedStaff && currentAssignedStaff.personnelId === staff.personnelId;

const getEventStaffWithRole =
  (eventStaff) =>
  (roleName) =>
  ({ personnelId }) =>
    eventStaff.find((staff) => staff.personnelId === personnelId && staff.roleName === roleName);

class AssignStaff extends Component {
  constructor(props) {
    super(props);

    this.state = {
      editStaff: false,
      searchValue: '',
      staffList: props.assignableStaffList,
      staffToBeAssigned: null,
      staffConfirmModalOpen: false,
    };

    this.personnelRoles = getPersonnelRoles();
  }

  componentDidUpdate(prevProps) {
    const { assignableStaffList } = this.props;

    if (prevProps.modalOpen !== this.props.modalOpen && !this.props.modalOpen) {
      this.setState({
        searchValue: '',
        staffList: this.props.assignableStaffList,
      });
    }

    if (!isEqual(prevProps.assignableStaffList, assignableStaffList)) {
      this.setState({
        staffList: assignableStaffList,
      });
    }
  }

  onSearchChange = (e) => {
    const { value } = e.target;
    const { staffList } = this.state;

    this.setState({
      searchValue: value,
      staffList: value === '' ? searchStaff(this.props.assignableStaffList, value) : staffList,
    });
  };

  onSearchStaff = () => {
    const { searchValue } = this.state;

    if (searchValue) {
      this.setState({
        staffList: searchStaff(this.props.assignableStaffList, searchValue),
      });
    }
  };

  onUpdateStaff = (staff, currentStaff, roleId, roomId, unassignFlag, omitConfirm, multiAssign) => {
    const sameRoomMultiAssign = this.props.eventStaff.find(
      (assignedStaff) =>
        assignedStaff.roomId === roomId && assignedStaff.personnelId === staff.personnelId
    );
    const { currentRole } = this.props;
    const isTDCRole = currentRole && currentRole.role === this.personnelRoles.TEST_DAY_COORDINATOR;
    if (
      staff.testCenterEventPersonnelId &&
      !omitConfirm &&
      !unassignFlag &&
      !isTDCRole &&
      (!staff.isTDC || (staff.isTDC && staff.roleName))
    ) {
      this.setState({
        staffConfirmModalOpen: true,
        staffToBeAssigned: staff,
      });
    } else {
      this.setState({
        staffToBeAssigned: null,
        staffConfirmModalOpen: false,
      });
      this.props.onUpdateStaff(
        staff,
        currentStaff,
        roleId,
        roomId,
        unassignFlag,
        omitConfirm,
        multiAssign,
        sameRoomMultiAssign
      );
    }
  };

  closeStaffConfirmModal = () => {
    this.setState({
      staffConfirmModalOpen: false,
    });
  };

  clearSearch = () => {
    this.setState({
      searchValue: '',
      staffList: this.props.assignableStaffList,
    });
  };

  render() {
    const {
      currentAssignedStaff,
      currentRole,
      currentRoom,
      eventStaff,
      mappedRoomTiming,
      modalOpen,
      tcEvent,
    } = this.props;

    const { searchValue, staffList, staffConfirmModalOpen, staffToBeAssigned } = this.state;

    const eventDetails = {
      testingEventName: tcEvent.testingEvent.name,
      locationName: currentRoom && currentRoom.location && currentRoom.location.name,
      locationArea: currentRoom && currentRoom.location && currentRoom.location.area,
    };
    const roles = this.personnelRoles;
    const isTDCRole = currentRole && currentRole.role === roles.TEST_DAY_COORDINATOR;
    const roomId = currentRoom && currentRoom.id;
    return (
      <div>
        <ModalInfo
          modalIsOpen={modalOpen}
          closeModal={() => this.props.toggleModal()}
          headerTitle={
            currentAssignedStaff && !isEmpty(currentAssignedStaff)
              ? this.props.t('staffAndRoomsManagement.assignStaff.editTitle')
              : this.props.t('staffAndRoomsManagement.assignStaff.assignTitle')
          }
          onSubmit={() => {}}
          className="modal-assign-staff"
          hideButtons>
          <section className="assign-staff">
            <div className="top-section">
              <h4>{currentRole && currentRole.name}</h4>
              {!isEmpty(currentRoom) && (
                <div>
                  <div className="room-info">
                    <span className="">{currentRoom.name}</span>
                  </div>
                  <div className="label-container">
                    <span className="label-box-radius">
                      {currentRoom.roomType
                        ? currentRoom.roomType.name
                        : this.props.t('testCenter.roomDashboard.noTypeLabel')}
                    </span>
                    {currentRoom.timing && (
                      <span className="label-box-radius">
                        {getRoomTimingName(currentRoom.timing, tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code, mappedRoomTiming)}
                      </span>
                    )}
                    {currentRoom.roomType && (
                      <span className="label-box-radius">
                        {currentRoom.roomType.onlineFlag
                          ? this.props.t('common.labels.online')
                          : this.props.t('common.labels.paper')}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <EventDetails {...eventDetails} displayTC={false} />
              <AlertNotice
                message="Test Day Coordinators do not need and cannot be assigned additional roles."
                inline
              />
            </div>
            <SearchForm
              className="staff-search"
              searchValue={searchValue}
              onChange={this.onSearchChange}
              onSearch={this.onSearchStaff}
              placeholder={this.props.t('staffAndRoomsManagement.assignStaff.searchPlaceholder')}
              displayIconButton={false}
              clearSearch={this.clearSearch}
            />
            <div className="staff-assign-list">
              <div className="staff-assign-header hide-mobile">
                <div>{this.props.t('staffAndRoomsManagement.assignStaff.name')}</div>
                <div>{this.props.t('staffAndRoomsManagement.assignStaff.role')}</div>
                {!isTDCRole && (
                  <div className="room-column">
                    {this.props.t('staffAndRoomsManagement.assignStaff.room')}
                  </div>
                )}
                <div></div>
              </div>
              {staffList.map((staff, idx) => {
                const tdcEventStaff = getEventStaffWithRole(eventStaff)(roles.TEST_DAY_COORDINATOR)(
                  staff
                );
                return (
                  <div className="staff-assign-row" key={`staff-item-${idx}`}>
                    <TooltipItem
                      item={`${staff.lastName}, ${staff.firstName}`}
                      customClass="fw-bold"
                    />
                    <div>
                      {(isTDCRole && !staff.isTDC) ||
                      (!isTDCRole && !staff.roleShortName && !tdcEventStaff) ? (
                        <div>-</div>
                      ) : (
                        <span className="label-box-radius">
                          {isTDCRole && staff.isTDC
                            ? currentRole.shortName
                            : tdcEventStaff?.roleShortName || staff.roleShortName}
                        </span>
                      )}
                    </div>
                    {!isTDCRole && (
                      <TooltipItem item={staff.roomName || '-'} customClass="room-column" />
                    )}
                    <div>
                      {!tdcEventStaff &&
                        !(isTDCRole && isCurrentUser(currentAssignedStaff, staff)) && (
                          <button
                            className="btn btn-outline btn-small"
                            onClick={() =>
                              this.onUpdateStaff(
                                staff,
                                currentAssignedStaff,
                                currentRole,
                                roomId,
                                isCurrentUser(currentAssignedStaff, staff)
                              )
                            }>
                            {isCurrentUser(currentAssignedStaff, staff) ? (
                              this.props.t('common.labels.unAssign')
                            ) : (
                              <>
                                <PlusIcon />
                                {this.props.t('common.labels.assign')}
                              </>
                            )}
                          </button>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </ModalInfo>
        <SimpleModal
          isOpen={staffConfirmModalOpen}
          onConfirm={() =>
            this.onUpdateStaff(
              staffToBeAssigned,
              currentAssignedStaff,
              currentRole,
              roomId,
              isCurrentUser(currentAssignedStaff, staffToBeAssigned),
              true,
              !this.props.testCenterCycle.cycle.supervisorMultiRoomNotAllowedFlag
            )
          }
          onCancel={this.closeStaffConfirmModal}
          confirmLabel={this.props.t('common.labels.assign')}
          cancelLabel={this.props.t('common.labels.cancel')}>
          {this.props.eventStaff.some(
            (assignedStaff) =>
              assignedStaff.personnelId === staffToBeAssigned?.personnelId &&
              assignedStaff.roomId === roomId
          ) ? (
            <div>
              <h4>
                {this.props.t('staffAndRoomsManagement.assignStaff.sameRoomMultiAssignTitle')}
              </h4>
              <p>{this.props.t('staffAndRoomsManagement.assignStaff.sameRoomMultiAssignText')}</p>
            </div>
          ) : !this.props.testCenterCycle.cycle.supervisorMultiRoomNotAllowedFlag ? (
            <div>
              <h4>{this.props.t('staffAndRoomsManagement.assignStaff.multiAssignTitle')}</h4>
              <p>{this.props.t('staffAndRoomsManagement.assignStaff.multiAssignText')}</p>
            </div>
          ) : (
            <div>
              <h4>{this.props.t('staffAndRoomsManagement.assignStaff.alreadyAssignedTitle')}</h4>
              <p>{this.props.t('staffAndRoomsManagement.assignStaff.alreadyAssignedText')}</p>
            </div>
          )}
        </SimpleModal>
      </div>
    );
  }
}

export default withTranslation()(AssignStaff);
AssignStaff.propTypes = {
  t: PropTypes.func.isRequired,
};

AssignStaff.defaultProps = {};
